import { ManipulatedVariable, Subcontroller } from '../types';
import { extractSheetXmlFromXlsx } from './xlsxService';

export interface ParsedMvRow {
  tag: string;
  description: string;
  controller: Subcontroller;
  processArea?: string;
  units: string;
  recommendedStepSize: number;
  approvedStepSize: number;
  notes: string;
  isValid: boolean;
  error?: string;
}

export function parseCsvLine(line: string, delimiter: string = ','): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === delimiter && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

function normalizeController(raw: string): Subcontroller {
  const val = (raw || '').trim();
  return val || 'General';
}

function mapTableRowsToParsedMvs(tableData: string[][]): {
  rows: ParsedMvRow[];
  totalParsed: number;
  validCount: number;
  errorCount: number;
} {
  if (tableData.length < 2) {
    return { rows: [], totalParsed: 0, validCount: 0, errorCount: 0 };
  }

  const rawHeaders = tableData[0].map((h) =>
    h.toLowerCase().replace(/[^a-z0-9]/g, '')
  );

  const getColIndex = (keywords: string[]) => {
    return rawHeaders.findIndex((h) => keywords.some((k) => h.includes(k)));
  };

  const ctrlIdx = getColIndex(['subcontroller', 'controller', 'ctrl']);
  const areaIdx = getColIndex(['processarea', 'area', 'section', 'system']);
  const tagIdx = getColIndex(['tag', 'mvid', 'variable', 'point']);
  const descIdx = getColIndex(['desc', 'name', 'label']);
  const unitsIdx = getColIndex(['unit', 'engunit', 'eu']);
  const recStepIdx = getColIndex(['recommendedstep', 'recstep', 'recommended', 'rec']);
  const appStepIdx = getColIndex(['approvedstep', 'appstep', 'approved', 'stepsize', 'step']);
  const notesIdx = getColIndex(['note', 'comment', 'remark']);

  const parsedRows: ParsedMvRow[] = [];

  for (let i = 1; i < tableData.length; i++) {
    const cols = tableData[i];
    if (cols.length === 0 || cols.every((c) => c === '')) continue;

    const rawController = (ctrlIdx !== -1 ? cols[ctrlIdx] : cols[0] || '').trim();
    const processArea = (areaIdx !== -1 ? cols[areaIdx] : cols[1] || '').trim();
    const tag = (tagIdx !== -1 ? cols[tagIdx] : cols[2] || '').trim();
    const description = (descIdx !== -1 ? cols[descIdx] : cols[3] || tag).trim();
    const units = (unitsIdx !== -1 ? cols[unitsIdx] : cols[4] || 'units').trim();
    const recStep = parseFloat(recStepIdx !== -1 ? cols[recStepIdx] : cols[5]) || 5.0;
    const appStep = parseFloat(appStepIdx !== -1 ? cols[appStepIdx] : cols[6]) || recStep || 5.0;
    const notes = (notesIdx !== -1 ? cols[notesIdx] : cols[7] || '').trim();

    const controller = normalizeController(rawController);

    let isValid = true;
    let error: string | undefined;

    if (!tag) {
      isValid = false;
      error = 'Missing MV Tag';
    }

    parsedRows.push({
      tag,
      description: description || tag,
      controller,
      processArea: processArea || undefined,
      units: units || 'units',
      recommendedStepSize: recStep,
      approvedStepSize: appStep,
      notes,
      isValid,
      error,
    });
  }

  const validCount = parsedRows.filter((r) => r.isValid).length;
  const errorCount = parsedRows.length - validCount;

  return {
    rows: parsedRows,
    totalParsed: parsedRows.length,
    validCount,
    errorCount,
  };
}

export function parseMvSpreadsheetText(fileContent: string): {
  rows: ParsedMvRow[];
  totalParsed: number;
  validCount: number;
  errorCount: number;
} {
  // Determine if it's Excel XML (SpreadsheetML) or CSV/TSV
  if (fileContent.includes('<Workbook') && fileContent.includes('<Worksheet')) {
    return parseExcelXml(fileContent);
  }

  // Otherwise parse as CSV / TSV
  const lines = fileContent.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length < 2) {
    return { rows: [], totalParsed: 0, validCount: 0, errorCount: 0 };
  }

  const firstLine = lines[0];
  const delimiter = firstLine.includes('\t') ? '\t' : ',';
  const tableData: string[][] = lines.map((l) => parseCsvLine(l, delimiter));

  return mapTableRowsToParsedMvs(tableData);
}

function parseExcelXml(xmlContent: string): {
  rows: ParsedMvRow[];
  totalParsed: number;
  validCount: number;
  errorCount: number;
} {
  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
  const rows = xmlDoc.getElementsByTagName('Row');

  if (rows.length < 2) {
    return { rows: [], totalParsed: 0, validCount: 0, errorCount: 0 };
  }

  const tableData: string[][] = [];
  for (let i = 0; i < rows.length; i++) {
    const cells = rows[i].getElementsByTagName('Cell');
    const rowVals: string[] = [];
    for (let c = 0; c < cells.length; c++) {
      const data = cells[c].getElementsByTagName('Data')[0];
      rowVals.push(data ? data.textContent || '' : '');
    }
    tableData.push(rowVals);
  }

  return mapTableRowsToParsedMvs(tableData);
}

// Parse modern OpenXML (.xlsx) ArrayBuffer
export async function parseMvXlsxBuffer(buffer: ArrayBuffer): Promise<{
  rows: ParsedMvRow[];
  totalParsed: number;
  validCount: number;
  errorCount: number;
}> {
  const { sheetXml, sharedStrings } = await extractSheetXmlFromXlsx(buffer);
  if (!sheetXml) {
    return { rows: [], totalParsed: 0, validCount: 0, errorCount: 0 };
  }

  const parser = new DOMParser();
  const xmlDoc = parser.parseFromString(sheetXml, 'text/xml');
  const rows = xmlDoc.getElementsByTagName('row');

  if (rows.length < 2) {
    return { rows: [], totalParsed: 0, validCount: 0, errorCount: 0 };
  }

  const getColIndexFromRef = (ref: string): number => {
    let col = 0;
    for (let i = 0; i < ref.length; i++) {
      const code = ref.charCodeAt(i);
      if (code >= 65 && code <= 90) {
        col = col * 26 + (code - 64);
      } else {
        break;
      }
    }
    return col - 1;
  };

  const getCellText = (cell: Element): string => {
    const t = cell.getAttribute('t');
    if (t === 'inlineStr') {
      const isElem = cell.getElementsByTagName('is')[0];
      return isElem ? isElem.textContent || '' : '';
    }
    if (t === 's') {
      const vElem = cell.getElementsByTagName('v')[0];
      if (vElem) {
        const idx = parseInt(vElem.textContent || '-1', 10);
        if (idx >= 0 && idx < sharedStrings.length) {
          return sharedStrings[idx];
        }
      }
      return '';
    }
    const vElem = cell.getElementsByTagName('v')[0];
    return vElem ? vElem.textContent || '' : cell.textContent || '';
  };

  const tableData: string[][] = [];
  for (let r = 0; r < rows.length; r++) {
    const rowElem = rows[r];
    const cells = rowElem.getElementsByTagName('c');
    const rowVals: string[] = [];
    for (let c = 0; c < cells.length; c++) {
      const cell = cells[c];
      const ref = cell.getAttribute('r') || '';
      const colIdx = ref ? getColIndexFromRef(ref) : c;
      while (rowVals.length < colIdx) {
        rowVals.push('');
      }
      rowVals[colIdx] = getCellText(cell).trim();
    }
    tableData.push(rowVals);
  }

  return mapTableRowsToParsedMvs(tableData);
}
